<template>
  <CCardBody>

    <alert-section
      :successAlertMessage="successAlertMessage"
      :dismissSecs="dismissSecs"
      :dismissSuccessAlert="dismissSuccessAlert"
      :errorAlertMessage="errorAlertMessage"
      :showErrorAlert="showErrorAlert"
    />

    <!-- Open Balance Alert -->
    <CAlert v-if="openBalanceAlert" color="warning" class="d-flex align-items-start">
      <div class="mr-3 mt-1">
        <CIcon name="cil-bell-exclamation" :height="24" class="text-warning" />
      </div>
      <div class="flex-grow-1">
        <div class="d-flex align-items-center mb-1">
          <strong class="mr-2">Restaurants with Open Balance</strong>
          <span class="badge badge-warning">{{ openBalanceAlert.list.length }}</span>
        </div>
        <p class="mb-2">{{ openBalanceAlert.message }}</p>
        <ul class="mb-2 pl-3">
          <li v-for="r in openBalanceAlert.list" :key="r.id">
            <CLink :to="{ name: 'View Restaurant', params: { id: r.id } }" target="_blank">
              #{{ r.id }} - {{ r.name }}
            </CLink>
            (<span :class="{'text-danger': r.balance < 0, 'text-dark': r.balance == 0}">{{ r.balance | toCurrency('remove_thousand_separator') }}</span>)
          </li>
        </ul>
        <div class="d-flex">
          <CButton size="sm" color="secondary" variant="outline" class="mr-2" @click="openBalanceAlert = null">Dismiss</CButton>
          <CButton size="sm" color="danger" @click="makeInvoices(true)">Ignore and Continue</CButton>
        </div>
      </div>
    </CAlert>

    <CAlert v-if="invoiceRunWarning" color="warning">
      <div class="d-flex justify-content-between align-items-start">
        <div>
          <strong>Invoice run completed with errors.</strong>
          <div>{{ invoiceRunWarning.message }}</div>
          <ul v-if="invoiceRunWarning.failedGroups.length" class="mb-0 mt-2 pl-3">
            <li v-for="group in invoiceRunWarning.failedGroups" :key="`${group.restaurant_id}-${group.billing_entity_id}`">
              Restaurant #{{ group.restaurant_id }}, entity #{{ group.billing_entity_id }}:
              {{ group.message }}
            </li>
          </ul>
        </div>
        <CButton size="sm" color="link" @click="invoiceRunWarning = null">Dismiss</CButton>
      </div>
    </CAlert>

    <!-- Filters -->
    <CCard class="filters" accent-color="warning">
      <CCardHeader
        role="button"
        class="text-warning shadow-none card-header"
        @click="resetFilters"
      >
        <strong class="m-0"><CIcon :name="`cil-filter${$store.state.filterShow ? '-x' : ''}`" /> Filters</strong>
        <div class="card-header-actions">
          <CIcon :name="`cil-chevron-${$store.state.filterShow ? 'bottom' : 'top'}`"/>
        </div>
      </CCardHeader>

      <CCollapse :show="$store.state.filterShow">
        <CCardBody class="p-2">
          <div class="d-flex flex-md-row flex-column" style="gap: 0.5rem;">

            <!-- Invoice Type Filter -->
            <div class="flex-grow-1">
              <v-select
                class="v-select-filter"
                placeholder="Select invoice type.."
                v-model="selectedInvoiceFilter"
                :reduce="o => o.value"
                :options="[
                  { value: 'all', label: 'All'},
                  { value: 'mailed', label: 'Mailed'},
                  { value: 'not-mailed', label: 'Not Mailed'},
                  { value: 'transfered', label: 'Transfered'},
                  { value: 'not-transfered', label: 'Not Transfered'},
                  { value: 'pay-to-customer', label: 'Pay to Customer'},
                ]"
              />
            </div>

            <!-- Billing Entity Filter -->
            <div class="flex-grow-1">
              <v-select
                class="v-select-filter"
                v-model="selectedBillingEntityId"
                :reduce="option => option.value"
                :options="billingEntityFilterOptions"
                :loading="billingEntitiesLoading"
                :clearable="false"
                @input="handleBillingEntityFilterChange"
              />
            </div>

            <!-- Restaurant Filter -->
            <div class="flex-grow-1">
              <v-select
                class="v-select-filter"
                placeholder="Select restaurant.."
                v-model="selectedRestaurant"
                :options="restaurants"
                :loading="loading && restaurants.length <= 0"
                @input="getAllItems"
              />
            </div>

            <!-- Pagination -->
            <div v-if="pages > 1">
              <CPagination
                align="end"
                :dots='false'
                :pages="pages"
                :active-page.sync="activePage"
              />
            </div>
          </div>
        </CCardBody>
      </CCollapse>
    </CCard>

    <div v-if="!loading">
      <CDataTable
        striped
        hover
        :items="loadedItems"
        :fields="fields"
        :sorter="{ external: true, resetable: true }"
        :table-filter="{ external: true, lazy: true, placeholder: '...' }"
        :tableFilterValue.sync="search"
        :column-filter="{ external: true, lazy: true }"
        :itemsPerPageSelect="{
          external: true,
          values: [5, 15, 25, 50, 100, 150, 200, 250, 500],
        }"
        :items-per-page.sync="itemsPerPage"
        :loading="loading"
        :noItemsView="{
          noResults: 'No filtering results are available!',
          noItems: 'No invoices found!',
        }"
        @update:sorter-value="sorterValue"
        @update:table-filter-value="tableSearch"
        @pagination-change="paginationChange"
        @row-clicked="rowClicked"
      >

        <!-- Check Box -->
        <template #select="{item}">
          <td class="text-right">
            <CInputCheckbox
              v-if="isSelectable(item)"
              class="ml-1"
              :checked="item._selected"
              @update:checked="() => select(item)"
              custom
            />
          </td>
        </template>

        <!-- ID -->
        <template #id="{item}">
          <td>
            #{{ item.id }}
          </td>
        </template>

        <!-- Sent  -->
        <template #ivMailed="{item}">
          <td class="text-center">
            <CIcon
              :height="32"
              :class="`text-${item.ivMailed ? 'success' : 'danger'}`"
              :name="`cil-${item.ivMailed ? 'check-alt' : 'x'}`"
            />

          </td>
        </template>

        <!-- Paid?  -->
        <template #paid="{item}">
          <td v-if="item.updated" class="text-center">
            <CSpinner color="primary" />
          </td>
          <td v-else class="text-center">
            <section class="is-paid" v-if="isShowPaid(item)">
              <CSwitch
                v-if="isShowMarkAsPaid(item)"
                color="success"
                shape="pill"
                :checked="item.restaurant.account_balance == 0"
                :disabled="item.restaurant.account_balance == 0"
                @update:checked="() => markAsPaid(item)"
              />
              <span v-else>{{ item.restaurant.account_balance * -1 }}</span>
            </section>
            <!-- <span v-else>***</span> -->
          </td>
        </template>


        <!-- Payment -->
        <template #payment="{item}">
          <td>
            <template v-if="item.ivPymntToCust > 0">
              <div>{{ item.ivPymntToCust | toCurrency('remove_thousand_separator') }}</div>
              <div>{{ item.ivCustIban | removeAllWhiteSpaces }}</div>
              <div>{{ item.ivName.replace(/\s+/g, '_') }}_{{ item.restaurant?item.restaurant.id:null }}</div>
              <div>{{ item.ivNr }}_derdengelden_menute_be</div>
            </template>
          </td>
        </template>

        <!-- Price -->
        <template #ivTotIncl="{item}">
          <td>
            {{ item.ivTotIncl | toCurrency('remove_thousand_separator') }}
          </td>
        </template>

        <!-- Invoice Number -->
        <template #ivNr="{item}">
          <td>
            <CLink @click="viewInvoice(item)">
              {{ item.ivNr }}
            </CLink>
          </td>
        </template>

        <!-- Date (ivDate) Filter -->
        <template #ivDate-filter>

          <div class="input-group mb-1">
            <div class="input-group-prepend">
              <span class="input-group-text p-1 w-min">From</span>
            </div>
            <input
              id="date-filter-from"
              type="date"
              v-model="filters.invoices.startDate"
              class="w-min"
            />
          </div>

          <div class="input-group">
            <div class="input-group-prepend">
              <span class="input-group-text p-1 w-min">To</span>
            </div>
            <input
              id="date-filter-from"
              type="date"
              v-model="filters.invoices.endDate"
              class="w-min"
            />
          </div>
        </template>

        <!-- Date (ivDate) -->
        <template #ivDate="{item}">
          <td :class="isToday(item.ivDate) ? 'text-danger font-weight-bold' : ''">
            {{ item.ivDate }}
          </td>
        </template>

        <!-- Restaurant -->
        <template #restaurant="{item}">
          <td>
            #{{ item.restaurant?item.restaurant.id:null }}
            <CLink class="font-weight-bold" target="_blank" :to="{name: 'View Restaurant',params: { id: item.restaurant?item.restaurant.id:null }}">
              {{ item.restaurant?item.restaurant.restaurant_name:null }}
            </CLink>
          </td>
        </template>

        <!-- Bank Transaction -->
        <template #txn="{item}">
          <td>
            <CButton v-if="item.bank_transaction"
              size="sm"
              color="link"
              v-c-tooltip="{ content: `Download TXN (#${item.txn_id})` }"
              @click="downloadTxnFile(item.bank_transaction.file_name)"
            >
              <CIcon name="cil-transfer" />
            </CButton>
          </td>
        </template>

        <!-- Actions -->
        <template #actions="{item}">
          <td class="py-2 text-center">
            <CButton
              size="sm"
              color="primary"
              shape="pill"
              v-c-tooltip="{
                html: true,
                content: 'Download'
              }"
              @click="viewInvoice(item)"
            >
              <CIcon name="cil-cloud-download" />
            </CButton>

            <CButton
              size="sm"
              color="info"
              shape="pill"
              class="ml-1"
              v-c-tooltip="{
                html: true,
                content: 'Send E-Mail'
              }"
              @click="sendInvoice(item)"
            >
              <CIcon name="cil-send" />
            </CButton>
          </td>
        </template>
      </CDataTable>
    </div>
    <div v-else class="mb-4">
      <CSpinner color="primary" />
    </div>

    <CToaster position="bottom-right" :autohide="3000" class="mb-5 mr-4">
      <CToast v-for="toast in toasts" :key="'toast' + toast" :show="true" color="warning">
        &#9757; {{ toastMessage }}
      </CToast>
    </CToaster>

    <CCard class="actions sticky-bottom">
      <CCardBody class="p-2">
        <div class="d-flex flex-wrap align-items-center" style="gap: 0.75rem">
          <div v-if="this.txnId && selectedInvoiceFilter == 'pay-to-customer'" class="action d-flex flex-gap-2">
            <CLink class="mb-1" @click="selectAllItems"><CIcon name="cil-library-add" /></CLink>
            <CLink @click="deselectItems"><CIcon name="cil-library" /></CLink>
            <CButton
              v-if="selectedItems.length"
              size="sm"
              color="info"
              shape="pill"
              @click="markAllAsPaid"
            >
              Mark as Paid
            </CButton>
          </div>

          <div>
            <h5 v-if="selectedItems.length" class="mt-1">Selected: <span class="d-inline count bg-primary pb-1">{{ selectedItems.length }}/{{ total }}</span></h5>
            <h5 v-else class="mt-1">Total: <span class="d-inline count bg-primary pb-1">{{ total }}</span></h5>
          </div>

          <div class="action">
            <CButton
              size="sm"
              color="success"
              @click="makeInvoicesModal = true"
            >
              <CIcon name="cil-send" /> Create Invoices
            </CButton>
          </div>

          <div class="action">
            <CButton
              size="sm"
              color="primary"
              v-c-tooltip="{
                html: true,
                content: `Download All Filtered Invoices`
              }"
              @click="downloadZip"
              :disabled="total == 0"
            >
              <CIcon name="cil-cloud-download" /> Download All
            </CButton>
          </div>

          <div class="action">
            <CButton
              size="sm"
              color="info"
              variant="ghost"
              @click="transferInvoice"
              :disabled="selectedBillingEntityId === null"
              v-c-tooltip="selectedBillingEntityId === null ? 'Select one billing entity before transferring.' : ''"
            >
              <CIcon name="cil-data-transfer-up" /> Transfer All Untransferred Invoices
            </CButton>
          </div>

          <div v-if="selectedInvoiceFilter == 'not-mailed'">
            <CButton
              size="sm"
              color="info"
              variant="ghost"
              @click="sendAllUnmailedInvoices"
              :disabled="selectedBillingEntityId === null"
              v-c-tooltip="selectedBillingEntityId === null ? 'Select one billing entity before sending bulk email.' : ''"
            >
              <CIcon name="cil-send" /> Send All Unmailed Invoices by Email
            </CButton>
          </div>

          <div v-if="pages > 1" class="ml-auto">
            <CPagination
              align="center"
              :dots='false'
              :pages="pages"
              :active-page.sync="activePage"
            />
          </div>
        </div>
      </CCardBody>
    </CCard>

    <CModal class="make-invoices" title="Create Invoices" size="sm" color="white" :show.sync="makeInvoicesModal">
      <CAlert color="info">
        This run creates invoices for <strong>all billing entities</strong>.
        The entity, restaurant and list filters do not limit invoice creation.
      </CAlert>
      <v-date-picker
        v-model="selectedDate"
        mode="date"
        title-position="left"
        is-inline
        is-expanded
        :first-day-of-week="1"
        :disabled-dates='{ weekdays: [1,2,3,4,5,6] }'
        is-required
      />
      <template #footer class="p-1">
        <CButton @click="makeInvoicesModal = false" color="link">Cancel</CButton>
        <CButton @click="makeInvoices" :disabled="selectedDate === null" color="info" shape="pill">
          <CIcon name="cil-send" /> Create invoices
        </CButton>
      </template>
    </CModal>

    <CModal
      class="modal-txn"
      title="SEPA results"
      size="lg"
      :show.sync="txnModal"
      :closeOnBackdrop="false"
      centered
    >
      <div v-if="txnFiles.length === 0" class="text-muted">No SEPA results were returned.</div>
      <div
        v-for="result in txnFiles"
        :key="`${result.billing_entity_id || 'legacy'}-${result.file || result.status}`"
        class="border rounded p-3 mb-2"
      >
        <div class="d-flex justify-content-between align-items-center">
          <strong>{{ result.billing_entity_code || `Entity #${result.billing_entity_id}` }}</strong>
          <CBadge :color="sepaStatusColor(result.status)">{{ result.status }}</CBadge>
        </div>
        <template v-if="result.status === 'created'">
          <div class="mt-2">File: {{ result.file }}</div>
          <div>Transactions: {{ result.transaction_count }}</div>
          <div>Control sum: EUR {{ formatControlSum(result.control_sum) }}</div>
          <CButton @click="downloadTxnFile(result.file)" size="sm" color="primary" class="mt-2">
            <CIcon name="cil-cloud-download" /> Download {{ result.billing_entity_code || 'SEPA' }}
          </CButton>
        </template>
        <div v-else class="mt-2" :class="result.status === 'failed' ? 'text-danger' : 'text-muted'">
          {{ result.message }}
        </div>
      </div>
      <template #footer class="p-1">
        <CButton :to="{ name: 'Bank Transactions' }" color="link">View All</CButton>
        <CButton @click="txnModal = false" size="sm" color="secondary">Close</CButton>
      </template>
    </CModal>

    <mc-spinner :opacity="0.8" v-show="submitted" :mtop="(32 + mcSpinnerMarginTop) + 'px'" />
  </CCardBody>
</template>

<script>
import { mapState } from 'vuex'
import { DatePicker } from 'v-calendar'
import _debounce from 'lodash/debounce';
import billingEntityInvoiceFilter from '@/helper/billingEntityInvoiceFilter'
import { formatControlSum, normalizeSepaResults, sepaStatusColor } from '@/helper/invoiceSepaResults'

export default {
  name: "BackendTable",
  mixins: [billingEntityInvoiceFilter],
  components: {
    'v-date-picker': DatePicker,
  },
  data() {
    return {
      loadedItems: [],
      fields: [
        { key: "actions",       label: "Actions",     filter: false, sorter: false, _style: "min-width: 100px; width: 100px; text-align: center;" },
        { key: "id",            label: "ID",          filter: false, _style: "min-width: 50px; width: 50px;" },
        { key: "ivMailed",      label: "Sent",        filter: false, _style: "min-width: 50px; width: 50px; text-align: center;" },
        { key: "ivNr",          label: "Inv.Number",  filter: false, _style: "min-width: 100px; width: 120px;" },
        { key: "billing_entity_code", label: "Entity", filter: false, _style: "min-width: 70px; width: 70px;" },
        { key: "ivTotIncl",     label: "Amount",      filter: false, _style: "min-width: 100px; width: 120px;" },
        { key: "ivDate",        label: "Date",        filter: true, _style: "min-width: 160px; width: 160px;" },
        { key: "payment",       label: "Payment",     filter: false, _style: "min-width: 240px;" },
        { key: "ivName",        label: "Inv.Name",    filter: false, _style: "min-width: 180px;" },
        { key: "restaurant",    label: "Restaurant",  filter: false, _style: "width: 200px;", sorter: false },
        { key: "txn",           label: "TXN",         filter: false, _style: "min-width: 50px; width: 50px;", sorter: false },
      ],

      activePage: 1,
      pages: 1,
      total: 0,
      items_per_page: null,
      orderBy: null,
      asc: null,
      search: null,
      filteredColumn: null,

      loading: false,
      submitted: false,

      // Alert işlemleri için
      successAlertMessage: {},
      dismissSecs: 10,
      dismissSuccessAlert: 0,
      errorAlertMessage: {},
      showErrorAlert: false,

      // Invoice Filter işlemleri için
      selectedInvoiceFilter: null,

      // Restaurants Filter işlemleri için
      restaurants: [],

      // Element Cover Spinner
      mcSpinnerMarginTop: 0,

      // Make Invoices
      makeInvoicesModal: false,
      selectedDate: null,
      lastUntilStr: null,

      // TXN
      txnModal: false,
      txnFileName: null,
      txnFiles: [],
      selectedItems: [],

      // For Toast Notifications
      toasts: 0,
      toastMessage: null,

      // Open balance pre-check
      openBalanceAlert: null,
      invoiceRunWarning: null,
    };
  },

  async beforeMount() {
    this.filterByDate = _debounce(async () => {
      await this.getAllItems();
    }, 1000);
  },

  async mounted() {
    this.activePage = this.queryPage;

    await Promise.all([
      this.getRestaurants(),
      this.loadBillingEntityFilter(),
    ]);

    if(this.restaurantId) {
      this.selectedRestaurant = this.restaurants.find((item) => item.value == this.restaurantId)
    }

    this.selectedInvoiceFilter = this.txnId ? 'pay-to-customer' : 'not-mailed';

    window.onscroll = () => {
      this.mcSpinnerMarginTop = document.documentElement.scrollTop;
    }
  },

  watch: {
    reloadParams() {
      if(this.queryPage != this.activePage) {
        this.$router.push({ name: this.$route.name, query: { page: this.activePage } });
      }
      this.onTableChange();
    },
    dateFilter() {
      this.filterByDate();
    },
    selectedInvoiceFilter: async function (newVal, oldVal) {
      if (newVal == "pay-to-customer") {
        // Add Paid (Betaald) field
        this.fields.splice(2, 0, { key: "paid", label: "Paid?", filter: false, _style: "width: 120px; text-align: center;" });
        this.txnId && this.fields.splice(0, 0, { key: "select", label: "", sorter: false, filter: false, _style: "width: 1%" });
      } else if (oldVal == "pay-to-customer") {
        this.txnId && this.fields.splice(0, 1); // Remove select field
        this.fields.splice(2, 1); // Remove Paid (Betaald) field
      }

      this.getAllItems();
    }
  },

  computed: {
    ...mapState(['filters']),

    itemsPerPage: {
      get: function () { return this.items_per_page ? this.items_per_page : parseInt(process.env.VUE_APP_ITEMS_PER_PAGE) },
      set: function (newValue) { this.items_per_page = newValue }
    },
    reloadParams() { return [this.activePage] },
    queryPage() { return parseInt(this.$route.query.page) || 1 },
    dateFilter() { return [this.filters.invoices.startDate, this.filters.invoices.endDate] },
    restaurantId () { return this.$route.params.restaurant_id },
    txnId() { return this.$route.params.txn_id },

    selectedRestaurant: {
      get() { return this.filters.invoices.selectedRestaurant },
      set(value) { this.filters.invoices.selectedRestaurant = value },
    },
  },

  methods: {
    async getAllItems(loading = true) {
      this.loading = loading;

      var url = new URL(this.$backend.INVOICES.GET_ALL),
        params = {
          page: this.activePage,
          itemsPerPage: this.itemsPerPage,
          search: this.search,
          orderBy: this.orderBy,
          asc: this.asc,
          invoiceFilter: this.selectedInvoiceFilter,
          restaurant: this.restaurantId || this.selectedRestaurant?.value,
          date_from: this.filters.invoices.startDate,
          date_to: this.filters.invoices.endDate,
          txn_id: this.txnId,
          ...this.billingEntityRequestParams(),
        };

      Object.keys(params).forEach((key) => {
        if (
          typeof params[key] !== "undefined" &&
          params[key] !== null &&
          params[key] !== "" &&
          params[key].length !== 0
        )
          url.searchParams.append(key, params[key]);
      });

      await this.$axios.get(url)
        .then((response) => {
          return response.data;
        })
        .then((items) => {
          // Son sayfadan daha büyük bir queryPage çağrımı (?page=XXX) yapılırsa;
          if(items.current_page > items.last_page)
            this.activePage = 1;
          else {
            this.loadedItems = items.data.map((el) => {
              return { ...el, _classes: '', updated: false };
            });
            this.activePage = items.current_page;
            this.pages = items.last_page;
            this.total = items.total;

            // Object.keys(items.data).map(function(key, index) {
            //   let item = items.data[index];
            //   item._classes = '';

            //   if (item.roDeleted) {
            //     item._classes += item.roPymntVfd == true ? 'strikeout ' : 'opacity-40 ';
            //   }
            //   if (item.roPymntVfd != true) {
            //     item._classes += 'table-warning ';
            //   }
            //   if (item.roConfirmationTime == null) {
            //     item._classes += 'text-info ';
            //   }
            // });
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },

    async getRestaurants() {
      const storedRestaurants = sessionStorage.getItem('restaurants');

      try {
        this.loading = true;
        const response = await this.$axios.get(this.$backend.RESTAURANT.GET_ALL_NAME);
        this.restaurants = response.data;
        sessionStorage.setItem('restaurants', JSON.stringify(this.restaurants));
      } catch (error) {
        this.restaurants = storedRestaurants ? JSON.parse(storedRestaurants) : [];
      } finally {
        this.loading = false;
      }
    },

    async viewInvoice(item) {
      this.submitted = true;

      await this.$axios
        .get(this.$backend.INVOICES.VIEW.replace("{id}", item.id), { responseType: 'blob' })
        .then((response) => {
          const fileName = `Factuur_${item.ivNr}.pdf`;

          // Download file from Blob
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', fileName); //or any other extension
          document.body.appendChild(link);
          link.click();
          link.remove();
          // <a href="blob:http://localhost:8080/f11c5655-6941-40b3-97bd-18152eb2a13b" download="Factuur_2022_15.pdf"></a>
        })
        .finally(() =>this.submitted = false);
    },

    async downloadZip() {
      // if (this.selectedRestaurant === null) {
      //   this.toastMessage = 'Please select a restaurant to download all invoices!';
      //   this.toasts++;
      //   return;
      // }
      if (!this.filters.invoices.startDate) {
        this.toastMessage = 'Please select a start date to download all invoices!';
        this.toasts++;
        return;
      }

      const confirm = await this.mSwal.fire({
        text: "All filtered invoices will be downloaded as a ZIP file.",
        title: "Are you sure you want to download all filtered invoices?",
        icon: "info",
        confirmButtonText: "Download",
        cancelButtonText: "Cancel"
      });

      if (confirm.isDismissed) return;

      this.submitted = true;

      var url = new URL(this.$backend.INVOICES.DOWNLOAD_ALL),
        params = {
          restaurant: (this.selectedRestaurant && this.selectedRestaurant.value) ? this.selectedRestaurant.value : null,
          invoiceFilter: this.selectedInvoiceFilter,
          date_from: this.filters.invoices.startDate,
          date_to: this.filters.invoices.endDate,
          search: this.search,
          ...this.billingEntityRequestParams(),
        };

      Object.keys(params).forEach((key) => {
        if (
          typeof params[key] !== "undefined" &&
          params[key] !== null &&
          params[key] !== "" &&
          params[key].length !== 0
        )
          url.searchParams.append(key, params[key]);
      });

      await this.$axios
        .get(url, { responseType: 'blob' })
        .then(({ data }) => {
          const fileName = `Facturen.zip`;

          // Download file from Blob
          const url = window.URL.createObjectURL(new Blob([data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', fileName);
          document.body.appendChild(link);
          link.click();
          link.remove();
        })
        .finally(() =>this.submitted = false);
    },

    async sendInvoice(item) {
      this.submitted = true;

      await this.$axios
        .get(this.$backend.INVOICES.SEND.replace("{id}", item.id))
        .then((response) => {
          item.ivMailed = true;

          this.showErrorAlert = false;
          this.successAlertMessage = {itemName: 'Success:', message: response.data.message};
          this.dismissSuccessAlert = this.dismissSecs;
        })
        .catch((error) => {
          this.errorAlertMessage = error.response.data;
          this.showErrorAlert = true;
        })
        .finally(() => {
          window.scrollTo(0,0);
          this.submitted = false;
        });
    },

    async sendAllUnmailedInvoices(){
      const confirm = await this.mSwal.fire({
        text: "All unmailed invoices will be sent via email to the respective restaurants.",
        title: "Are you sure you want to send all unmailed invoices?",
        icon: "question",
        confirmButtonText: "Send",
        cancelButtonText: "Cancel"
      });

      if (confirm.isDismissed) return;

      this.submitted = true;

      await this.$axios
        .post(this.$backend.INVOICES.SEND_ALL_UNMAILED, this.billingEntityRequestParams())
        .then((response) => {
          this.showErrorAlert = false;
          this.successAlertMessage = {itemName: 'Success:', message: response.data};
          this.dismissSuccessAlert = this.dismissSecs;
        })
        .catch((error) => {
          this.errorAlertMessage = error.response.data;
          this.showErrorAlert = true;
        })
        .finally(() => {
          window.scrollTo(0,0);
          this.submitted = false;
        });
    },

    async transferInvoice() {
      const confirm = await this.mSwal.fire({
        text: "All untransferred invoices will be transferred to the accounting system.",
        title: "Are you sure you want to transfer all untransferred invoices?",
        icon: "warning",
        confirmButtonText: "Confirm",
        cancelButtonText: "Cancel"
      });

      if (confirm.isDismissed) return;

      this.submitted = true;

      await this.$axios
        .post(this.$backend.INVOICES.TRANSFER, this.billingEntityRequestParams())
        .then((response) => {
          this.getAllItems();
          this.showErrorAlert = false;
          this.successAlertMessage = {itemName: 'Success:', message: response.data.message};
          this.dismissSuccessAlert = this.dismissSecs;
        })
        .catch((error) => {
          this.errorAlertMessage = error.response.data;
          this.showErrorAlert = true;
        })
        .finally(() => {
          window.scrollTo(0,0);
          this.submitted = false;
        });
    },

    async makeInvoices(ignoreOpenBalance = false) {
      this.makeInvoicesModal = false;
      this.submitted = true;

      // Determine until string; cache for possible ignore flow
      const untilStr = this.selectedDate
        ? new Date(this.selectedDate).toISOString().split('T')[0]
        : (this.lastUntilStr || null);
      this.lastUntilStr = untilStr;

      await this.$axios
        .post(this.$backend.INVOICES.MAKE, {
          until: untilStr,
          ignore_open_balance: ignoreOpenBalance
        })
        .then(({data}) => {
          this.txnFiles = this.normalizeSepaResults(data);
          this.txnFileName = data.sepa_file || null;
          if (this.txnFiles.length > 0) {
            this.txnModal = true;
          }
          this.getAllItems();
          this.showErrorAlert = false;
          this.successAlertMessage = {itemName: 'Success:', message: data.message};
          this.dismissSuccessAlert = this.dismissSecs;
          this.invoiceRunWarning = data.status === 'partial_success'
            ? {
                message: data.message,
                failedGroups: data.failed_groups || [],
              }
            : null;
          
          // Clear cached date on success
          this.selectedDate = null;
          this.lastUntilStr = null;
          this.openBalanceAlert = null;
        })
        .catch((error) => {
          if (error.response && error.response.data && error.response.data.open_balance_restaurants) {
            this.openBalanceAlert = {
              message: error.response.data.message,
              list: error.response.data.open_balance_restaurants || []
            };
          } else {
            this.errorAlertMessage = error.response?.data || 'An error occurred while creating invoices.';
            this.showErrorAlert = true;
          }
        })
        .finally(() => {
          window.scrollTo(0,0);
          this.submitted = false;
        });
    },

    normalizeSepaResults,
    sepaStatusColor,
    formatControlSum,

    async markAsPaid(invoice) {
      try {
        invoice.updated = true;

        await this.$axios.post(this.$backend.PAYMENTS.STORE, {
          amount: Math.abs(invoice.ivPymntToCust),
          type: '1',
          restaurant: invoice.ivRestId,
          invoice_id: invoice.id,
          billing_entity_id: invoice.billing_entity_id,
          note: 'Inv.Number: ' + invoice.ivNr
        });

        const accountBalanceResponse = await this.$axios.get(this.$backend.RESTAURANT.ACCOUNT_BALANCE.replace("{id}", invoice.ivRestId));

        this.$set(invoice.restaurant, 'account_balance', accountBalanceResponse.data);
        this.showErrorAlert = false;
        this.successAlertMessage = { itemName: 'Payment', message: 'has been successfully created.' };
        this.dismissSuccessAlert = this.dismissSecs;
        this.deselect(invoice);
      } catch (error) {
        this.errorAlertMessage = error.response?.data || 'An error occurred while creating the payment.';
        this.showErrorAlert = true;
      } finally {
        this.$set(invoice, 'updated', false);
      }
    },

    async markAllAsPaid() {
      const confirm = await this.mSwal.fire({
        text: "Are you sure you want to continue?",
        title: "The selected invoices will be marked as paid?",
        icon: "warning",
        confirmButtonText: "Confirm",
        cancelButtonText: "Cancel"
      });

      if (confirm.isDismissed) return;

      try {
        this.selectedItems.forEach(invoice => {
          this.$set(invoice, 'updated', true);
        });

        const transformedInvoices = this.selectedItems.map(invoice => ({
          amount: Math.abs(invoice.ivPymntToCust),
          type: '1',
          restaurant: invoice.ivRestId,
          invoice_id: invoice.id,
          billing_entity_id: invoice.billing_entity_id,
          note: 'Inv.Number: ' + invoice.ivNr
        }));

        await this.$axios.post(this.$backend.PAYMENTS.STORE_MULTIPLE, transformedInvoices);
      } catch (error) {
        this.errorAlertMessage = error.response?.data || 'An error occurred while creating the payment.';
        this.showErrorAlert = true;
      } finally {
        this.deselectItems();
        await this.getAllItems(false);
      }
    },

    async downloadTxnFile(fileName) {
      this.submitted = true;
      this.txnModal = false;

      await this.$axios
        .get(this.$backend.BANK_TRANSACTIONS.DOWNLOAD.replace("{file_name}", fileName), { responseType: 'blob' })
        .then((response) => {
          // Download file from Blob
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', fileName); //or any other extension
          document.body.appendChild(link);
          link.click();
          link.remove();
        })
        .finally(() => {
          this.submitted = false;
          this.$toast.success(`SEPA file downloaded.`);
        });
    },



    onTableChange() { // Active Page güncellendiğinde çağrılıyor.
      this.deselectItems();
      this.getAllItems();
    },
    paginationChange(value) { // Items Per Page güncellendiğinde çağrılıyor.
      this.itemsPerPage = value;
      this.onTableChange();
    },
    sorterValue(item) {
      this.orderBy = item.column;
      this.asc = item.column ? item.asc : null; //column null ise asc=null olsun
      this.onTableChange();
    },
    tableSearch() {
      this.onTableChange();
    },

    isShowPaid(item) {
      return item.restaurant?.latest_invoice && item.restaurant.latest_invoice.id == item.id;
    },
    isShowMarkAsPaid(item) {
      return item.restaurant.account_balance * -1 == item.restaurant.latest_invoice.ivPymntToCust || item.restaurant.account_balance == 0;
    },
    isSelectable(item) {
      return this.isShowMarkAsPaid(item) && item.restaurant.account_balance != 0;
    },
    rowClicked(item, index, column, e) {
      const checkTarget = !['INPUT', 'LABEL', 'BUTTON', 'svg', 'path', 'A', 'IMG', 'SPAN'].includes(e.target.tagName);
      if (checkTarget && this.isSelectable(item)) {
        this.select(item)
      }
    },
    select(item) {
      this.$set(item, "_selected", !item._selected);

      if (item._selected) {
        item._classes = item._classes.concat(" table-selected");
        this.selectedItems.push(item);
      } else {
        item._classes = item._classes.replace(" table-selected", "");
        this.selectedItems.splice(this.selectedItems.indexOf(item), 1);
      }
    },
    deselect(item) {
      this.$set(item, "_selected", false);
      item._classes = item._classes.replace(" table-selected", "");
      this.selectedItems.splice(this.selectedItems.indexOf(item), 1);
    },
    selectAllItems() {
      this.deselectItems();

      this.loadedItems
        .filter(this.isSelectable)
        .forEach(item => {
          this.$set(item, "_selected", true);
          item._classes = item._classes.concat(" table-selected");
          this.selectedItems.push(item);
        });
    },
    deselectItems() {
      this.loadedItems
        .filter(this.isSelectable)
        .forEach(this.deselect);
    },

    // Filters
    resetFilters() {
      if (this.$store.state.filterShow) {
        this.selectedRestaurant = null;
        this.selectDefaultBillingEntity();
        this.getAllItems();
      }
      this.$store.commit('toggle', 'filterShow');
    },

    handleBillingEntityFilterChange() {
      this.activePage = 1;
      this.getAllItems();
    },

    // Helper functions
    isToday(dateString) {
      const someDate = new Date(dateString);
      const today = new Date()
      return someDate.getDate() == today.getDate() &&
        someDate.getMonth() == today.getMonth() &&
        someDate.getFullYear() == today.getFullYear()
    }
  },
};
</script>

<style>
.table th, .table td {
  padding: 0.5rem 0.25rem;
  vertical-align: middle;
}
@media (min-width: 576px) {
  input[aria-label='table filter input'] {
    width: 280px !important;
  }
}

.make-invoices .modal-header {
  padding: 0.75rem !important;
}
.make-invoices .modal-footer {
  padding: 0.25rem !important;
}
.make-invoices .vc-container {
  box-shadow: none !important;
  border: none !important;
}

.is-paid .c-switch-input:disabled ~ .c-switch-slider {
  opacity: 1;
}

/* Modal TXN */
.modal-txn .modal-body {
  display: none;
}
</style>

<style scoped>
/* Date Filter */
span.w-min {
  width: 48px;
}
input.w-min {
  width: 100px;
}
input[type="date" i],
input[type="datetime-local" i] {
  color: #f9b115;
}
input[type="date"]::-webkit-calendar-picker-indicator,
input[type="datetime-local"]::-webkit-calendar-picker-indicator {
  padding-left: 4px;
  margin: 0;
}

.actions .action {
  border-right: 1px solid #d8dbe0 !important;
  padding-right: 0.75rem !important;
}
</style>
